import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useTranslation } from '@/hooks/use-translation'
import { analytics } from '@/lib/analytics/analytics.service'

interface Translations {
  emoji: string
  title: string
  subtitle: string
  technicalErrorLabel: string
  unknownError: string
  solutionsIntro: string
  solutions: {
    turnOffOn: string
    pray: string
    clearCache: string
    contactSupport: string
  }
  buttons: {
    goHome: string
    reload: string
  }
  footer: string
}

interface Props {
  children: ReactNode
  translations: Translations
}

interface State {
  hasError: boolean
  error?: Error
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('💥 Error capturado por ErrorBoundary:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    })
    analytics.captureException(error, {
      source: 'ErrorBoundary',
      componentStack: errorInfo.componentStack ?? '',
    })
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: undefined })
    window.location.href = '/'
  }

  render(): ReactNode {
    const { translations } = this.props

    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full p-8 text-center space-y-6 bg-card border-border shadow-xl">
            <div className="space-y-4">
              <div className="text-6xl animate-bounce">{translations.emoji}</div>
              <h1 className="text-4xl font-bold text-foreground">{translations.title}</h1>
              <p className="text-xl text-muted-foreground">{translations.subtitle}</p>
            </div>

            <div className="bg-muted/30 rounded-lg p-6 border border-border">
              <p className="text-sm text-muted-foreground mb-2 font-mono">
                {translations.technicalErrorLabel}
              </p>
              <code className="text-sm text-destructive font-mono break-all">
                {this.state.error?.message || translations.unknownError}
              </code>
            </div>

            <div className="space-y-3">
              <p className="text-muted-foreground">{translations.solutionsIntro}</p>
              <ul className="text-left space-y-2 text-sm text-muted-foreground max-w-md mx-auto">
                <li className="flex items-start gap-2">
                  <span className="text-primary">✓</span>
                  <span>{translations.solutions.turnOffOn}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">✓</span>
                  <span>{translations.solutions.pray}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">✓</span>
                  <span>{translations.solutions.clearCache}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">✓</span>
                  <span>{translations.solutions.contactSupport}</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Button
                onClick={this.handleReset}
                size="lg"
                className="bg-primary hover:bg-primary-hover text-primary-foreground transition-smooth"
              >
                {translations.buttons.goHome}
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                size="lg"
                className="transition-smooth"
              >
                {translations.buttons.reload}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground pt-4">{translations.footer}</p>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  const t = useTranslation()

  const translations: Translations = {
    emoji: t('errorBoundary.emoji'),
    title: t('errorBoundary.title'),
    subtitle: t('errorBoundary.subtitle'),
    technicalErrorLabel: t('errorBoundary.technicalErrorLabel'),
    unknownError: t('errorBoundary.unknownError'),
    solutionsIntro: t('errorBoundary.solutionsIntro'),
    solutions: {
      turnOffOn: t('errorBoundary.solutions.turnOffOn'),
      pray: t('errorBoundary.solutions.pray'),
      clearCache: t('errorBoundary.solutions.clearCache'),
      contactSupport: t('errorBoundary.solutions.contactSupport'),
    },
    buttons: {
      goHome: t('errorBoundary.buttons.goHome'),
      reload: t('errorBoundary.buttons.reload'),
    },
    footer: t('errorBoundary.footer'),
  }

  return <ErrorBoundaryClass translations={translations}>{children}</ErrorBoundaryClass>
}
